import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { keyframes } from 'tss-react';
import { makeStyles } from 'tss-react/mui';
import { getParticipantDisplayName } from '../../../base/participants/functions';
import { keyPressed, mouseClicked, mouseMoved, mouseScrolled, setControllerEngaged, stopController } from '../../actions';
import { EVENTS } from '../../constants';
import { getKey, getModifiers, toSharedScreenPosition } from '../../functions';
import ControllerBar from './ControllerBar';
import { useVideoRect } from './useVideoRect';
/**
 * How close together two presses of Escape have to be to mean "give the
 * keyboard back" rather than "send Escape to the remote machine".
 */
const RELEASE_GESTURE_WINDOW = 600;
const useStyles = makeStyles()(theme => {
    return {
        surface: {
            position: 'absolute',
            // Above the video, below the toolbar and filmstrip: the surface
            // covers only the picture, and the app's own controls stay usable.
            zIndex: 3,
            borderRadius: '4px',
            // Nothing here should be selectable or draggable: every gesture
            // belongs to the machine at the other end.
            userSelect: 'none',
            WebkitUserSelect: 'none',
            touchAction: 'none'
        },
        allowed: {
            cursor: 'pointer',
            background: 'transparent',
            boxShadow: `inset 0 0 0 2px ${theme.palette.action01}66`,
            transition: 'box-shadow .16s ease-out'
        },
        engaged: {
            // A crosshair says the pointer means something different here: it
            // is not selecting anything on this page, it is pointing at another
            // computer.
            cursor: 'crosshair',
            animation: `${keyframes `
                0%, 100% {
                    box-shadow: inset 0 0 0 2px ${theme.palette.action01},
                                inset 0 0 28px -8px ${theme.palette.action01};
                }
                50% {
                    box-shadow: inset 0 0 0 2px ${theme.palette.action01},
                                inset 0 0 44px 0 ${theme.palette.action01};
                }
            `} 2.8s ease-in-out infinite`,
            '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
                boxShadow: `inset 0 0 0 2px ${theme.palette.action01},
                            inset 0 0 32px -6px ${theme.palette.action01}`
            }
        },
        prompt: {
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: theme.spacing(1),
            padding: `${theme.spacing(3)} ${theme.spacing(4)}`,
            borderRadius: '10px',
            background: `${theme.palette.ui01}E6`,
            backdropFilter: 'blur(12px)',
            boxShadow: '0 12px 40px -8px rgba(0, 0, 0, .6)',
            textAlign: 'center',
            pointerEvents: 'none'
        },
        promptTitle: {
            ...theme.typography.bodyShortBoldLarge,
            color: theme.palette.text01
        },
        promptHint: {
            ...theme.typography.bodyShortRegular,
            color: theme.palette.text02
        }
    };
});
/**
 * The layer that turns pointer and keyboard events over the shared screen into
 * remote control messages.
 *
 * It replaces the jQuery handlers upstream attached to the whole window. Those
 * were removed with `$(window).off('keydown')`, which took every other window
 * key handler in the application with them.
 *
 * @returns {ReactElement|null}
 */
const RemoteControlSurface = () => {
    const { classes, cx } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const rect = useVideoRect();
    const { controlled, engaged, isCapturingEvents } = useSelector((state) => state['features/remote-control'].controller);
    const controlledName = useSelector((state) => (controlled ? getParticipantDisplayName(state, controlled) : ''));
    const surfaceRef = useRef(null);
    const lastEscape = useRef(0);
    const pendingMove = useRef(undefined);
    const moveFrame = useRef(0);
    // A session can be live while its screen is off the stage: the controller
    // has looked at someone else, so events are paused. The capture layer goes
    // away with the picture, but the bar must not — holding control of
    // somebody's computer with nothing on screen saying so is exactly how it
    // gets forgotten about.
    const active = Boolean(controlled);
    const capturing = active && isCapturingEvents;
    const engage = useCallback(() => {
        dispatch(setControllerEngaged(true));
    }, [dispatch]);
    const release = useCallback(() => {
        dispatch(setControllerEngaged(false));
    }, [dispatch]);
    // Pointer movement is coalesced to one message per frame. Upstream sent one
    // endpoint message per mousemove event, which on a trackpad is hundreds a
    // second and floods the data channel the video is also using.
    const flushMove = useCallback(() => {
        moveFrame.current = 0;
        const position = pendingMove.current;
        pendingMove.current = undefined;
        if (position) {
            dispatch(mouseMoved(position.x, position.y));
        }
    }, [dispatch]);
    const onPointerMove = useCallback((event) => {
        if (!engaged) {
            return;
        }
        const position = toSharedScreenPosition(event.clientX, event.clientY);
        if (!position) {
            return;
        }
        pendingMove.current = position;
        if (!moveFrame.current) {
            moveFrame.current = window.requestAnimationFrame(flushMove);
        }
    }, [engaged, flushMove]);
    const onPointerDown = useCallback((event) => {
        if (!engaged) {
            engage();
            return;
        }
        event.preventDefault();
        // Capturing the pointer keeps a drag alive after it leaves the video.
        // Without it a click-and-drag that ends anywhere else on the page would
        // never deliver its mouseup, and the remote machine would be left with
        // the button still held.
        try {
            event.currentTarget.setPointerCapture(event.pointerId);
        }
        catch (error) {
            // Capture is a nicety; the ordinary case works without it.
        }
        dispatch(mouseClicked(EVENTS.mousedown, event.button + 1));
    }, [dispatch, engage, engaged]);
    const onPointerUp = useCallback((event) => {
        if (!engaged) {
            return;
        }
        event.preventDefault();
        try {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
        catch (error) {
            // Nothing was captured, which is fine.
        }
        dispatch(mouseClicked(EVENTS.mouseup, event.button + 1));
    }, [dispatch, engaged]);
    const onDoubleClick = useCallback((event) => {
        if (!engaged) {
            return;
        }
        event.preventDefault();
        dispatch(mouseClicked(EVENTS.mousedblclick, event.button + 1));
    }, [dispatch, engaged]);
    const onContextMenu = useCallback((event) => {
        // The right button belongs to the remote machine, so this page must not
        // answer it with a menu of its own.
        event.preventDefault();
    }, []);
    // Wheel and key handling are attached by hand rather than through React,
    // because both need preventDefault and React registers wheel listeners
    // passively.
    useEffect(() => {
        const surface = surfaceRef.current;
        if (!surface || !engaged) {
            return;
        }
        const onWheel = (event) => {
            event.preventDefault();
            dispatch(mouseScrolled(event.deltaX, event.deltaY));
        };
        surface.addEventListener('wheel', onWheel, { passive: false });
        return () => surface.removeEventListener('wheel', onWheel);
    }, [dispatch, engaged]);
    useEffect(() => {
        if (!engaged) {
            return;
        }
        const onKeyDown = (event) => {
            // Escape is a key the remote machine wants, so a single press is
            // forwarded. Pressing it twice in quick succession is the way back
            // out — the one gesture that is always available, whatever the
            // remote machine is doing with the keyboard.
            if (event.key === 'Escape') {
                const now = Date.now();
                if (now - lastEscape.current < RELEASE_GESTURE_WINDOW) {
                    lastEscape.current = 0;
                    event.preventDefault();
                    release();
                    return;
                }
                lastEscape.current = now;
            }
            const key = getKey(event);
            if (!key) {
                return;
            }
            event.preventDefault();
            dispatch(keyPressed(EVENTS.keydown, key, getModifiers(event)));
        };
        const onKeyUp = (event) => {
            const key = getKey(event);
            if (!key) {
                return;
            }
            event.preventDefault();
            dispatch(keyPressed(EVENTS.keyup, key, getModifiers(event)));
        };
        // Losing focus means the keystrokes are going somewhere this page
        // cannot see, so control is handed back rather than half-held.
        const onBlur = () => release();
        window.addEventListener('keydown', onKeyDown, true);
        window.addEventListener('keyup', onKeyUp, true);
        window.addEventListener('blur', onBlur);
        return () => {
            window.removeEventListener('keydown', onKeyDown, true);
            window.removeEventListener('keyup', onKeyUp, true);
            window.removeEventListener('blur', onBlur);
        };
    }, [dispatch, engaged, release]);
    // A session that ends, or a screen that leaves the stage, must not leave the
    // page swallowing the controller's keyboard.
    useEffect(() => {
        if (!capturing && engaged) {
            release();
        }
    }, [capturing, engaged, release]);
    useEffect(() => () => {
        if (moveFrame.current) {
            window.cancelAnimationFrame(moveFrame.current);
        }
    }, []);
    const onStop = useCallback(() => {
        dispatch(stopController(true));
    }, [dispatch]);
    if (!active) {
        return null;
    }
    return (React.createElement(React.Fragment, null,
        capturing && rect && React.createElement("div", { className: cx(classes.surface, engaged ? classes.engaged : classes.allowed), onContextMenu: onContextMenu, onDoubleClick: onDoubleClick, onPointerDown: onPointerDown, onPointerMove: onPointerMove, onPointerUp: onPointerUp, ref: surfaceRef, style: {
                height: rect.height,
                left: rect.left,
                top: rect.top,
                width: rect.width
            } }, !engaged && (React.createElement("div", { className: classes.prompt },
            React.createElement("span", { className: classes.promptTitle }, t('remoteControl.takeControl')),
            React.createElement("span", { className: classes.promptHint }, t('remoteControl.takeControlHint', { user: controlledName }))))),
        React.createElement(ControllerBar, { engaged: engaged, onRelease: release, onStop: onStop, paused: !isCapturingEvents })));
};
export default RemoteControlSurface;
